/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
} from '@mui/material';
import {
  getFieldProps,
  InputPhone,
  getFormProps,
  Validate,
  useForm,
  getSubmitButtonProps,
  TimezoneSelector,
} from '@marageti/z4-lib';
import { Phone, TimeZone } from '@marageti/z4-sdk/lib/standards';
import { Controller, FieldValues } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { solidButtonSx } from '../../styles';

export interface ConfirmPhoneViewProps {
  defaultValues: {
    phone: Phone;
    timeZone: TimeZone;
  };
  onSubmit: (data: FieldValues) => void;
}

const titleSx = {
  fontFamily: 'Satoshi, sans-serif',
  fontWeight: 500,
  fontSize: '28px',
  lineHeight: '40px',
  letterSpacing: 0,
  textAlign: 'center',
  color: '#070707',
} as const;

const schema = z.object({
  phone: z.object({
    prefix: z.string(),
    number: z.string(),
  }).refine((value) => {
    if (!value.number) return true;
    return Validate.phone(`${value.prefix}${value.number}`);
  }, { message: 'Please enter a valid number.' }),
  timeZone: z.object({
    name: z.string(),
    rawOffsetInMinutes: z.any(),
  })
    .refine((data) => data.name && data.rawOffsetInMinutes !== undefined, {
      message: 'Required',
      path: [],
    }),
});

const ConfirmPhoneView = ({
  defaultValues,
  onSubmit,
}: ConfirmPhoneViewProps): React.ReactElement => {
  const {
    handleSubmit,
    formState: {
      errors, isSubmitting, isValid,
    },
    control,
    reset,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  return (
    <Box
      component="form"
      {...getFormProps(handleSubmit(onSubmit))}
      sx={{
        width: '100%',
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        '& .MuiOutlinedInput-root': {
          borderRadius: 0,
        },
        '& .PhoneInput': {
          borderRadius: 0,
        },
      }}
    >
      <Stack spacing={2} alignItems="center" width="100%" sx={{ pt: 4 }}>
        <Typography component="h1" sx={titleSx}>
          Confirm your phone number
        </Typography>
        <Typography color="text.secondary" textAlign="center">
          Specialists often start with a short call to tailor your trip.
        </Typography>
      </Stack>

      <Stack spacing={2} width="100%" sx={{ mt: '40px' }}>
        <Controller
          control={control}
          name="phone"
          render={({ field }) => (
            <InputPhone
              id="phone"
              label="Phone Number"
              {...field}
              {...getFieldProps('phone', errors)}
            />
          )}
        />

        <Controller
          name="timeZone"
          control={control}
          render={({ field }) => (
            <TimezoneSelector
              required
              id="timeZone-selector"
              label="Timezone"
              {...getFieldProps('timeZone', errors)}
              {...field}
            />
          )}
        />
      </Stack>

      <Box sx={{ mt: 'auto', pt: 4 }}>
        <LoadingButton
          variant="contained"
          type="submit"
          fullWidth
          sx={solidButtonSx}
          {...getSubmitButtonProps(isSubmitting, isValid)}
        >
          Confirm
        </LoadingButton>
      </Box>
    </Box>
  );
};

export default ConfirmPhoneView;
